import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api';
import { queryKeys } from './query-client';
import { loadGoogleFont } from './font-loader';
import {
  DEFAULT_THEME_SETTINGS,
  themeToCssVariables,
  type ThemeSettings,
} from '@mn/shared';

interface ThemePayload {
  settings: ThemeSettings;
  cssVariables: Record<string, string>;
}
interface ThemeContextValue {
  settings: ThemeSettings;
  isLoading: boolean;
  refresh: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  settings: DEFAULT_THEME_SETTINGS,
  isLoading: true,
  refresh: () => {},
});

function applyCssVariables(vars: Record<string, string>) {
  const root = document.documentElement;
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: queryKeys.publicTheme(),
    queryFn: async () => (await apiClient.get<{ data: ThemePayload }>('/public/theme')).data,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const settings = data?.settings ?? DEFAULT_THEME_SETTINGS;
  const cssVariables = data?.cssVariables ?? themeToCssVariables(DEFAULT_THEME_SETTINGS);

  useEffect(() => {
    applyCssVariables(cssVariables);
    loadGoogleFont(settings.fonts.body);
    loadGoogleFont(settings.fonts.heading);
  }, [cssVariables, settings.fonts.body, settings.fonts.heading]);

  return (
    <ThemeContext.Provider value={{ settings, isLoading, refresh: () => void refetch() }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
